import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { CAFE_INFO, WHATSAPP_ORDER_URL } from "../data/cafe";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/rolls", label: "Rolls" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={CAFE_INFO.logo}
            alt="mismos"
            className="h-12 w-12 rounded-full ring-1 ring-white/10 group-hover:ring-[#FF8C5A]/50 transition"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-xl tracking-wide">
              {CAFE_INFO.name}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
              Daily Dose of Chill
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm tracking-wide transition relative ${
                  isActive
                    ? "text-white"
                    : "text-white/60 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-warm-gradient" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
          <a
            href={WHATSAPP_ORDER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-order-cta"
            className="ml-4 px-5 py-2.5 rounded-full bg-warm-gradient text-black text-sm font-medium hover:opacity-90 transition"
          >
            Order Now
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <MenuIcon size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-lg font-display ${
                    isActive ? "text-gradient" : "text-white/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={WHATSAPP_ORDER_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-nav-order"
              className="mt-4 py-3 px-5 rounded-full bg-warm-gradient text-black text-center font-medium"
            >
              Order Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
